<template>
  <div>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left color="primary" class="mr-2"
                >mdi-message-text</v-icon
              >
              Chat History
            </v-card-title>
            <PageExplanation page-key="ChatHistory" />
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="jobId"
                    label="Job ID"
                    type="number"
                    outlined
                    clearable
                    prepend-inner-icon="mdi-numeric"
                    @keyup.enter="loadChatHistory"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6" class="d-flex align-start">
                  <v-btn
                    color="primary"
                    :loading="loading"
                    :disabled="!jobId"
                    @click="loadChatHistory"
                    large
                    class="mr-2"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Load Chat History
                  </v-btn>
                  <v-btn
                    v-if="chatData && chatData.totalMessages > 0"
                    color="secondary"
                    outlined
                    @click="exportChat"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Export
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Job Info Card -->
      <v-row v-if="chatData && chatData.jobInfo">
        <v-col cols="12">
          <v-card outlined>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <div class="text-caption grey--text">Job ID</div>
                  <div class="text-h6">{{ chatData.jobInfo.id }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption grey--text">Status</div>
                  <div>
                    <v-chip
                      small
                      :color="getStatusColor(chatData.jobInfo.status)"
                      text-color="white"
                    >
                      {{ chatData.jobInfo.statusLabel }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption grey--text">Shift Date</div>
                  <div class="text-h6">
                    {{ formatDate(chatData.jobInfo.startDate) }}
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption grey--text">Location</div>
                  <div class="text-body-1">
                    {{ chatData.jobInfo.locationName || "N/A" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error Message -->
      <v-row v-if="error">
        <v-col cols="12">
          <v-alert type="error" dismissible @input="error = null">
            {{ error }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Chat Conversations (multi-segment) -->
      <v-row
        v-if="chatData && chatData.conversations && chatData.conversations.length > 0"
      >
        <v-col cols="12">
          <div
            v-for="(conversation, cIdx) in chatData.conversations"
            :key="cIdx"
            class="mb-4"
          >
            <v-card outlined>
              <v-card-title class="py-2">
                <v-icon left color="primary">mdi-message-reply-text</v-icon>
                <span v-if="conversation.segmentLabel">
                  Chat with {{ conversation.hygienistName }} &mdash;
                  {{ conversation.segmentLabel }}
                </span>
                <span v-else>
                  Messages ({{ chatData.totalMessages }})
                </span>
                <v-spacer></v-spacer>
                <v-chip small outlined>
                  {{ conversation.messages.length }} message{{
                    conversation.messages.length !== 1 ? "s" : ""
                  }}
                </v-chip>
              </v-card-title>

              <v-divider
                v-if="chatData.conversations.length > 1"
              ></v-divider>

              <v-card-text>
                <div
                  class="chat-container"
                  style="max-height: 500px; overflow-y: auto;"
                >
                  <div
                    v-for="(message, mIdx) in conversation.messages"
                    :key="mIdx"
                    class="message-item mb-3"
                  >
                    <v-card
                      :color="
                        message.authorType === 'talent'
                          ? 'green lighten-5'
                          : 'blue lighten-5'
                      "
                      outlined
                      class="pa-3"
                    >
                      <div
                        class="d-flex justify-space-between align-start mb-2"
                      >
                        <div>
                          <v-chip
                            small
                            :color="
                              message.authorType === 'talent'
                                ? 'green'
                                : 'blue'
                            "
                            text-color="white"
                            class="mr-2"
                          >
                            <v-icon small left>mdi-account</v-icon>
                            {{ message.authorName }}
                          </v-chip>
                          <v-chip small outlined>
                            {{
                              message.authorType === "talent"
                                ? "Talent"
                                : "Office"
                            }}
                          </v-chip>
                        </div>
                        <div class="text-caption grey--text">
                          {{ formatDateTime(message.sentAt) }}
                        </div>
                      </div>
                      <v-divider class="my-2"></v-divider>
                      <div class="text-body-1" style="white-space: pre-wrap;">
                        {{ message.body }}
                      </div>
                    </v-card>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <v-row
        v-if="
          chatData &&
          chatData.conversations &&
          chatData.conversations.length === 0 &&
          !loading &&
          jobId
        "
      >
        <v-col cols="12">
          <v-alert type="info">
            No chat messages found for Job ID {{ jobId }}.
          </v-alert>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Vue from "vue";
import { getChatHistory } from "../../api";
import { formatInTimeZone } from "date-fns-tz";
import { currentTimeZone } from "@/helpers";
import { logger } from "@/utils/logger";

export default Vue.extend({
  name: "ChatHistory",
  data: () => {
    return {
      jobId: null,
      loading: false,
      chatData: null,
      error: null
    };
  },
  methods: {
    async loadChatHistory() {
      if (!this.jobId) {
        this.error = "Please enter a Job ID";
        return;
      }

      this.loading = true;
      this.error = null;
      this.chatData = null;

      try {
        logger.debug("Loading chat history for job", { jobId: this.jobId });
        const data = await getChatHistory(parseInt(this.jobId, 10));
        this.chatData = data;
        logger.info("Chat history loaded successfully", {
          jobId: this.jobId,
          totalMessages: data.totalMessages || 0,
          segments: data.conversations?.length || 0
        });
      } catch (error) {
        logger.error("Error loading chat history", error, {
          jobId: this.jobId
        });
        this.error = this.resolveChatHistoryError(error);
        this.chatData = null;
      } finally {
        this.loading = false;
      }
    },

    resolveChatHistoryError(error) {
      const fallbackMessage =
        "We could not find chat history for this Job ID. Confirm the ID and try again.";
      if (!error) return fallbackMessage;

      const message = error.message || "";
      const normalizedMessage = message.toLowerCase();
      if (
        normalizedMessage.includes("not found") ||
        normalizedMessage.includes("404")
      ) {
        return "Job not found.";
      }

      return message || "Failed to load chat history. Please check if the Job ID is correct.";
    },

    getStatusColor(status) {
      const colors = {
        1: "grey",
        2: "orange",
        3: "#0B4598",
        4: "#13CA82",
        5: "grey darken-1",
        6: "grey darken-2",
        7: "#FB7672"
      };
      return colors[status] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return formatInTimeZone(
          new Date(dateString),
          currentTimeZone(),
          "yyyy-MM-dd"
        );
      } catch (e) {
        return dateString;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      try {
        return formatInTimeZone(
          new Date(dateString),
          currentTimeZone(),
          "yyyy-MM-dd HH:mm:ss zzz"
        );
      } catch (e) {
        return dateString;
      }
    },

    exportChat() {
      if (!this.chatData || !this.chatData.totalMessages) return;

      const exportData = {
        jobId: this.chatData.jobInfo?.id || this.jobId,
        jobInfo: this.chatData.jobInfo,
        conversations: this.chatData.conversations.map(conv => ({
          hygienistName: conv.hygienistName,
          segmentLabel: conv.segmentLabel,
          messages: conv.messages.map(msg => ({
            timestamp: this.formatDateTime(msg.sentAt),
            author: msg.authorName,
            authorType: msg.authorType,
            message: msg.body
          }))
        }))
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json"
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `chat-history-job-${this.jobId}-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
});
</script>

<style scoped>
.chat-container {
  padding: 16px;
}

.message-item {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
